import { useContext, useEffect, useState } from "react";
import Page from "./Page";
import LoginContext from "../LoginContext";
import { useNavigate } from "react-router-dom";
import { createNewReportGroup, getPaymentUrlForReportGroup, validateQuarterSection } from "../api";
import { parseAts } from "../ats";
import ReportForView from "../components/ReportForView";
import { useCallback } from "react";

import './CreateMultiReportPage.css'

function ReportRow({index, updateRow, removeRow}) {
    const [reportName, setReportName] = useState('')
    const [location, setLocation] = useState('')
    const [locationValid, setLocationValid] = useState(false)
    const [validating, setValidating] = useState(false)
    const [error, setError] = useState(false)

    const updateReportName = useCallback((newName) => {
        setReportName(newName)
        updateRow(index, {reportName: newName, location, valid: newName.length>2 && locationValid})
    }, [location, locationValid, reportName, updateRow])

    const updateLocation = useCallback((newLocation) => {
        setLocation(newLocation)
        setLocationValid(false)
        updateRow(index, {reportName, location: newLocation, valid: false})
    }, [location, locationValid, reportName, updateRow])

    useEffect(() => {
        setLocationValid(false)
        setError(false)
        if (location.length > 15) {
            try {
                parseAts(location) // will explode if format is bad
                setValidating(true)
                validateQuarterSection(location).then(() => {
                    setLocationValid(true)
                    updateRow(index, { reportName, location, valid: reportName.length>2})
                    setValidating(false)
                })
                .catch((error)=> {
                    setError(true)
                    setValidating(false)
                })
            }
            catch (error) {
                setError(true)
                setValidating(false)
            }
        }
    }, [location])

    return (
        <tr>
            <td>
                 <input type="text"  className="form-control form-control-sm" name="reportName" id="reportName" 
                    value={reportName} onChange={(e) => updateReportName(e.target.value) }
                />
            </td>
            <td>
                <div className="d-flex">
                    <input type="text"  className={"form-control form-control-sm " + (error?"text-danger":"") } name="location" id="location" 
                        value={location} onChange={(e) => updateLocation(e.target.value) }
                    />
                    <div>
                        { validating && <div className="ps-2 pt-1"><div className="text-warning spinner-border spinner-border-sm"></div></div>}
                        { locationValid && <div className="ps-2 pt-1"><i className="text-success bi-check-lg" /></div>}
                        { error && <div className="ps-2 pt-1"><i className="text-warning bi-exclamation-circle-fill" /></div>}
                    </div>
                </div>
            </td>
            <td>
                <button className="btn btn-tertiary btn-sm" onClick={() => removeRow(index)}><i className="text-danger bi-x-circle"/></button>
            </td>
        </tr>
    )
}

export default function CreateMultiReportPage() {
    const {user} = useContext(LoginContext)
    const navigate = useNavigate()

    const [reportFor, setReportFor] = useState({ 
        fullName: user.fullName,
        email: user.email,
        companyName: user.companyName,
        address: user.address,
        city: user.city,
        province: user.province,
        postalCode: user.postalCode
    })
    
    const [editingCompany, setEditingCompany] = useState(reportFor.companyName?.length ? false : true)

    const [groupName, setGroupName] = useState('')
    const [rows, setRows] = useState([])

    const [startingPurchase, setStartingPurchase] = useState(false)
    const [generationError, setGenerationError] = useState('')

    useEffect(() => {
        if (rows.length == 0) {
            addRow()
        }
    }, [rows])

    const assembleGroupDto = () => {
        const reports = []
        for(const row of rows) {
            reports.push({
                projectName: row.reportName,
                location: row.location,
                ...reportFor
            })
        }       
        return {
            groupName,
            reports
        }
    }

    const startCreateReportGroup = () => {
        const groupDto = assembleGroupDto()
        createNewReportGroup(groupDto)
        .then((newReportGroup) => {
            navigate(`/reportGroup/${newReportGroup.id}`)
        })
        .catch((error) => {
            setGenerationError(error.message)
        })
    }

    const startPurchase = () => {
        setStartingPurchase(true)
        const groupDto = assembleGroupDto()
        createNewReportGroup(groupDto)
        .then((newReportGroup) => {
            return getPaymentUrlForReportGroup(newReportGroup.id)
        })
        .then(({ paymentUrl }) => {
            // off to pay!
            window.location.href = paymentUrl
        })
        .catch((error) => {
            setGenerationError(error.message)
            setStartingPurchase(false)
        })
        .finally(() => {
        })
    }

    const addRow = () => {
        setRows([...rows, {
            updateTime: Date.now(),
            reportName: '',
            location: '',
            valid: false
        }])
    }

    const updateRow = (index, {reportName, location, valid }) => {
        rows[index].reportName = reportName
        rows[index].location = location
        rows[index].valid = valid
        setRows([...rows])
    }

    const removeRow = (index) => {
        const newRows = [...rows]
        newRows.splice(index, 1)
        setRows([...newRows])
    }

    let generateValid = true
    rows.forEach(row => {
        generateValid &= row.valid
    })
    generateValid &= groupName.length > 2

    return (
        <Page title="AERAS - Create Report Batch">
            <div className="row pt-2">
                <ul className="nav nav-tabs">
                    <li className="nav-item">
                        <a className="nav-link" href="/createReport">Single Report</a>
                    </li>
                    <li className="nav-item">
                        <a className="nav-link  active" aria-current="page" href="#" >Multiple Reports</a>
                    </li>
                </ul>
            </div>
            <div className="row pt-3">
                <div className="col-12 col-md-6 mb-2">
                    <div className="">
                        <label className="h6" htmlFor="groupName">Batch Name</label>
                        <input type="text"  className="form-control" name="groupName" id="groupName" 
                            value={groupName} onChange={(e) => setGroupName(e.target.value)}
                        />
                    </div>
                    <label className="mt-2 h6">Reports</label>
                    <table className="table">
                        <thead><tr>
                            <th className="small not-bold">Name</th>
                            <th className="small not-bold">Quarter Section or LSD</th>
                            <th></th>
                        </tr></thead>
                        <tbody className="no-row-borders">
                            {rows.map((row,index) => (
                                <ReportRow 
                                    key={row.updateTime} 
                                    index={index}
                                    updateRow={updateRow}
                                    removeRow={removeRow}
                                />
                            ))}
                            <tr>
                                <td>
                                    <button className="btn btn-secondary mt-1" onClick={addRow} >
                                        <i className='bi-plus-lg'></i> Add
                                    </button>
                                </td>
                                <td colSpan="2">
                                    <div className="form-text">Example Format: <b>SW-21-024-01-W5M</b> or <b>06-21-024-01-W5M</b></div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                </div>
                <div className="col">
                    <h6 className="mb-0">Prepare for</h6>
                    <ReportForView 
                        reportFor={reportFor} setReportFor={setReportFor} 
                        editing={editingCompany} setEditing={setEditingCompany}
                    />
                </div>
                { generationError ? <div className="col-12 text-danger">{generationError}</div> : null}
                { !editingCompany && (
                    <div className="col-12">
                        { user.subscriber && <button className="btn btn-primary" disabled={!generateValid} onClick={() => startCreateReportGroup()}>Generate Reports</button> }
                        { !user.subscriber && <button className="btn btn-primary" disabled={!generateValid  || startingPurchase} onClick={() => startPurchase()} >
                            Buy Reports {startingPurchase && <div className="ms-1 spinner-border spinner-border-sm"></div>} 
                        </button> }
                        <button className="btn btn-tertiary ms-2" onClick={() => navigate('/reports')}>Cancel</button>
                    </div>
                )}
            </div>
        </Page>
    )

}